import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, MessageSquare, Scale, ChevronDown, History, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import ChatHistoryPanel from '../components/ChatHistoryPanel';
import { saveConversation, loadConversations } from '../lib/chatHistory';
import { useChatKeyboard } from '../lib/nativeKeyboard';

const ACTIVE_CONVO_KEY = 'mibeko_active_conversation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stable id generator for messages : avoids array-index-as-key issues when
// messages get inserted/reordered (loading states, retries, deletions).
let messageCounter = 0;
const nextMessageId = () => `msg_${Date.now()}_${++messageCounter}`;

const markdownComponents = {
  h1: ({ children }) => (
    <h2 className="md-h1">{children}</h2>
  ),
  h2: ({ children }) => (
    <h2 className="md-h2">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="md-h3">{children}</h3>
  ),
  p: ({ children }) => (
    <p className="md-p">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="md-ul">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="md-ol">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="md-li">{children}</li>
  ),
  blockquote: ({ children }) => (
    <blockquote className="md-blockquote">{children}</blockquote>
  ),
  strong: ({ children }) => (
    <strong className="md-strong">{children}</strong>
  ),
  em: ({ children }) => (
    <em className="md-em">{children}</em>
  ),
  code: ({ inline, children }) => (
    inline
      ? <code className="md-code-inline">{children}</code>
      : <pre className="md-code-block"><code>{children}</code></pre>
  ),
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer" className="md-link">{children}</a>
  ),
  hr: () => <hr className="md-hr" />,
};

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const toWhatsAppText = (markdown) =>
  markdown
    .replace(/\*\*(.+?)\*\*/g, '*$1*')
    .replace(/^>\s?/gm, '')
    .replace(/^#{1,4}\s?/gm, '')
    .trim();

const WELCOME_TEXT = "Bonjour ! Je suis votre assistant juridique specialise dans le droit congolais.\n\nJe peux analyser votre situation et vous indiquer les articles de loi applicables de la **Constitution**, du **Code de la Famille**, du **Code Penal** et du **Code du Travail** de la RDC.\n\nDecrivez votre situation ou posez votre question juridique.";
const makeWelcomeMessage = () => {
  let name = '';
  try { name = localStorage.getItem('mibeko_user_name') || ''; } catch {}
  const content = name ? WELCOME_TEXT.replace('Bonjour !', `Bonjour ${name} !`) : WELCOME_TEXT;
  return { id: nextMessageId(), role: 'bot', content, cited: [] };
};

const ChatAssistant = () => {
  useChatKeyboard();
  // Restaure la conversation active (ex : retour apres consultation d'un article cite)
  const initial = (() => {
    try {
      const activeId = sessionStorage.getItem(ACTIVE_CONVO_KEY);
      if (activeId) {
        const convo = loadConversations().find((c) => c.id === activeId);
        if (convo?.messages?.length) return convo;
      }
    } catch {}
    return null;
  })();

  const [messages, setMessages] = useState(() => initial ? initial.messages : [makeWelcomeMessage()]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(() => initial ? initial.id : `conv_${Date.now()}`);
  const [sessionId, setSessionId] = useState(() => (initial && initial.sessionId) || `session_${Date.now()}`);
  const [showCited, setShowCited] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Sauvegarde locale de la conversation des qu'elle contient un message utilisateur
  useEffect(() => {
    const firstUser = messages.find((m) => m.role === 'user');
    if (!firstUser) return;
    try { sessionStorage.setItem(ACTIVE_CONVO_KEY, conversationId); } catch {}
    saveConversation({
      id: conversationId,
      sessionId,
      title: firstUser.content.slice(0, 80),
      updatedAt: new Date().toISOString(),
      messages,
    });
  }, [messages, conversationId, sessionId]);

  const openConversation = (convo) => {
    setMessages(convo.messages?.length ? convo.messages : [makeWelcomeMessage()]);
    setConversationId(convo.id);
    setSessionId(convo.sessionId || `session_${Date.now()}`);
    try { sessionStorage.setItem(ACTIVE_CONVO_KEY, convo.id); } catch {}
    setShowCited({});
    setShowHistory(false);
  };

  const startNewConversation = () => {
    setMessages([makeWelcomeMessage()]);
    setConversationId(`conv_${Date.now()}`);
    setSessionId(`session_${Date.now()}`);
    try { sessionStorage.removeItem(ACTIVE_CONVO_KEY); } catch {}
    setShowCited({});
    setShowHistory(false);
  };

  const suggestions = [
    "Mon employeur m'a licencie sans preavis, est-ce legal ?",
    "Quel est l'age minimum pour se marier en RDC ?",
    "Quelles sont les conditions pour devenir avocat ?",
    "Quel preavis pour quitter ma maison en location ?",
    "Je veux creer une startup, quelles sont les demarches ?",
    "Mon voisin m'a frappe, que dit la loi ?",
    "Quels sont mes droits en cas de divorce ?",
    "Comment fonctionne l'heritage en RDC ?",
  ];

  const handleSend = async (text) => {
    const q = text || input.trim();
    if (!q || loading) return;

    setMessages(prev => [...prev, { id: nextMessageId(), role: 'user', content: q, cited: [] }]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q, session_id: sessionId }),
      });
      const data = await res.json();

      const botMsg = data.response || "Desole, je n'ai pas pu traiter votre question. Veuillez reformuler.";
      const cited = data.cited_articles || [];

      setMessages(prev => [...prev, { id: nextMessageId(), role: 'bot', content: botMsg, cited }]);
    } catch (err) {
      console.error('[Chat] /api/chat request failed:', err?.message || err);
      setMessages(prev => [...prev, {
        id: nextMessageId(),
        role: 'bot',
        content: "Erreur de connexion au serveur. Veuillez verifier votre connexion internet et reessayer.",
        cited: [],
      }]);
    }
    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSend();
  };

  const toggleCited = (idx) => {
    setShowCited(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const shareOnWhatsApp = (index) => {
    const answer = messages[index];
    const question = messages[index - 1]?.role === 'user' ? messages[index - 1].content : '';
    const parts = [];
    if (question) parts.push(`*Question :* ${question}`);
    parts.push(toWhatsAppText(answer.content));
    parts.push("— Réponse de Mibeko, l'application du droit congolais\nhttps://mibekordc.com");
    const url = `https://wa.me/?text=${encodeURIComponent(parts.join('\n\n'))}`;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <div className="chat-container">
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        background: 'var(--bg-white)',
      }}>
        <div className="icon-circle" style={{ width: '40px', height: '40px' }}>
          <Scale style={{ width: '18px', height: '18px' }} />
        </div>
        <div>
          <h1 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-dark)' }} data-testid="chat-title">
            Assistant Juridique IA
          </h1>
          <p style={{ fontSize: '11px', color: 'var(--text-gray)' }}>
            Analyse basee sur les textes officiels de la RDC
          </p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
          <button
            onClick={startNewConversation}
            data-testid="chat-new-conversation-btn"
            title="Nouvelle conversation"
            style={{
              background: 'none', border: '1px solid var(--border)', borderRadius: '8px',
              cursor: 'pointer', color: 'var(--text-gray)', padding: '7px', display: 'flex',
            }}
          >
            <Plus style={{ width: 16, height: 16 }} />
          </button>
          <button
            onClick={() => setShowHistory(true)}
            data-testid="chat-history-btn"
            title="Historique des conversations"
            style={{
              background: 'none', border: '1px solid var(--border)', borderRadius: '8px',
              cursor: 'pointer', color: 'var(--text-gray)', padding: '7px', display: 'flex',
            }}
          >
            <History style={{ width: 16, height: 16 }} />
          </button>
        </div>
      </div>

      {showHistory && (
        <ChatHistoryPanel
          onClose={() => setShowHistory(false)}
          onOpen={openConversation}
          onNew={startNewConversation}
          activeId={conversationId}
        />
      )}

      {/* Messages */}
      <div className="chat-messages" data-testid="chat-messages">
        {messages.map((msg, i) => (
          <div key={msg.id}>
            <div
              className={`chat-bubble ${msg.role === 'user' ? 'user' : 'bot'}`}
              data-testid={`chat-msg-${i}`}
            >
              {msg.role === 'bot' ? (
                <div className="md-content">
                  <ReactMarkdown components={markdownComponents}>
                    {msg.content}
                  </ReactMarkdown>
                </div>
              ) : msg.content}
            </div>

            {/* Share on WhatsApp (bot answers only, not the welcome message) */}
            {msg.role === 'bot' && i > 0 && (
              <button
                onClick={() => shareOnWhatsApp(i)}
                data-testid={`share-whatsapp-btn-${i}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                  marginTop: '6px', padding: '5px 10px',
                  background: 'rgba(37, 211, 102, 0.12)', color: '#1DA851',
                  border: '1px solid rgba(37, 211, 102, 0.35)', borderRadius: '999px',
                  fontSize: '12px', fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                <WhatsAppIcon /> Partager sur WhatsApp
              </button>
            )}

            {/* Cited articles */}
            {msg.role === 'bot' && msg.cited && msg.cited.length > 0 && (
              <div style={{ maxWidth: '85%', marginTop: '4px' }}>
                <button
                  onClick={() => toggleCited(i)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'var(--primary)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 0',
                    fontFamily: 'inherit',
                  }}
                  data-testid={`cited-toggle-${i}`}
                >
                  <ChevronDown style={{
                    width: '14px',
                    height: '14px',
                    transform: showCited[i] ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.2s',
                  }} />
                  {msg.cited.length} article{msg.cited.length > 1 ? 's' : ''} cite{msg.cited.length > 1 ? 's' : ''}
                </button>
                {showCited[i] && (
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px',
                    marginTop: '4px',
                  }}>
                    {msg.cited.map((cite, ci) => (
                      <Link
                        key={`${msg.id}-cite-${cite.id || ci}`}
                        to={`/article/${cite.id}`}
                        data-testid={`cited-article-link-${i}-${ci}`}
                        style={{
                          padding: '4px 10px',
                          background: 'var(--bg-icon)',
                          color: 'var(--primary)',
                          fontSize: '11px',
                          fontWeight: 600,
                          borderRadius: '6px',
                          textDecoration: 'none',
                          border: '1px solid transparent',
                          transition: 'border-color 0.15s, background-color 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; }}
                      >
                        {cite.article_number} — {cite.domain_label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="chat-bubble bot" style={{ opacity: 0.7 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
              <div style={{
                width: '8px', height: '8px', borderRadius: '50%',
                background: 'var(--primary)',
                animation: 'spin 1s linear infinite',
              }} />
              Analyse juridique en cours...
            </div>
          </div>
        )}

        {messages.length === 1 && !loading && (
          <div style={{ padding: '8px 0' }}>
            <p style={{ fontSize: '12px', color: 'var(--text-gray)', marginBottom: '8px', fontWeight: 600 }}>
              Questions frequentes :
            </p>
            <div className="chat-suggestions" data-testid="chat-suggestions">
              {suggestions.map(s => (
                <button
                  key={s}
                  className="chat-suggestion-btn"
                  onClick={() => handleSend(s)}
                  data-testid="suggestion-btn"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form className="chat-input-bar" onSubmit={handleSubmit} data-testid="chat-input-form">
        <input
          type="text"
          placeholder="Decrivez votre situation juridique..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={loading}
          data-testid="chat-input"
        />
        <button
          type="submit"
          className="chat-send-btn"
          disabled={!input.trim() || loading}
          data-testid="chat-send-btn"
        >
          <Send style={{ width: '18px', height: '18px' }} />
        </button>
      </form>
    </div>
  );
};

export default ChatAssistant;
